import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Paper,
  TextField,
  Button,
  Typography,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  useTheme,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  CircularProgress,
  alpha,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import BusinessIcon from "@mui/icons-material/Business";
import PersonPinIcon from "@mui/icons-material/PersonPin";
import DnsIcon from "@mui/icons-material/Dns";
import TerminalIcon from "@mui/icons-material/Terminal";
import HttpOutlinedIcon from "@mui/icons-material/HttpOutlined";
import {
  QUANTIDADE_DESTINOS_PING,
  MACRO_PING_HABILITADO,
  MACROS_DESTINO,
  MACROS_ORIGEM,
} from "../../../../../utils/pingMacros";
import api from "../../../../../services/api";
import PingPresetSelect from "../PingPresetSelect";
import validarIp from "../../../../../utils/validadorIp";
import VincularClientesStep from "../VincularClientesStep";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", md: 750 },
  bgcolor: "secondary.darker",
  boxShadow: 24,
  p: 4,
  maxHeight: "95vh",
  display: "flex",
  flexDirection: "column",
  borderRadius: "20px",
  
};

const findCaseInsensitive = (target, options) => {
  if (!target) return "";
  if (!Array.isArray(options)) return "";
  return (
    options.find(
      (opt) => opt.toLowerCase() === target.toString().toLowerCase(),
    ) || ""
  );
};

export default function EditHostModal({
  open = false,
  onClose = () => {},
  onSave = () => {},
  templates = [],
  hostData = null,
  loading = false,
  proxys = [],
  pingIcons = [],
  pingDefaults = [],
  hostClientsMap = {},
  setHostClientsMap = () => {},
}) {
  const [activeStep, setActiveStep] = useState(0);

  const initialFormState = {
    id: null,
    nome: "",
    ip: "",
    comunidadeSnmp: "",
    porta: 161,
    fabricante: "",
    modelo: "",
    userSsh: "",
    senhaSsh: "",
    portaSsh: 22,
    httpAddress: "",
    httpUser: "",
    httpPass: "",
    proxy: "0",
    locatedAt: "CLIENTE",
  };

  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [modelosDisponiveis, setModelosDisponiveis] = useState([]);
  const [pingHabilitado, setPingHabilitado] = useState(false);
  const [destinosPing, setDestinosPing] = useState(
    Array(QUANTIDADE_DESTINOS_PING).fill(null),
  );
  const [origensPing, setOrigensPing] = useState(
    Array(QUANTIDADE_DESTINOS_PING).fill(""),
  );
  const [origensErrors, setOrigensErrors] = useState(
    Array(QUANTIDADE_DESTINOS_PING).fill(""),
  );
  const [loadingMacros, setLoadingMacros] = useState(false);
  const theme = useTheme();

  const textFieldStyle = {
    borderRadius: "8px",
    "& .MuiFilledInput-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? theme.palette.secondary.dark
          : theme.palette.secondary.light,
      borderRadius: "8px",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? theme.palette.secondary.darker
            : theme.palette.secondary.light,
      },
    },
    "& .MuiFilledInput-root.Mui-disabled": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? theme.palette.secondary.dark
          : theme.palette.secondary.light,
    },
    "& .MuiInputLabel-root": {
      color:
        theme.palette.mode === "dark"
          ? theme.palette.colors?.gray?.light || "#aaa"
          : theme.palette.secondary.dark,
    },
    "& .MuiFilledInput-input": {
      color:
        theme.palette.mode === "dark"
          ? theme.palette.white.main
          : theme.palette.colors?.black?.dark || "#000",
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor:
        theme.palette.mode === "dark"
          ? theme.palette.colors?.gray?.light || "#aaa"
          : theme.palette.colors?.black?.medium || "#555",
    },
  };

  const isAdmin = useMemo(() => {
    try {
      const userStorage = localStorage.getItem("user");
      if (!userStorage) return false;
      const userObj = JSON.parse(userStorage);
      const usuario = userObj.usuario || userObj;
      return usuario.rlPermissoesUsuario?.some(
        (p) => p.permissao?.codigoPermissao?.toUpperCase() === "ADM",
      );
    } catch (e) {
      return false;
    }
  }, []);

  const enablePings =
    form?.fabricante === "MIKROTIK" ||
    form?.fabricante === "DATACOM" ||
    form?.fabricante === "HUAWEI";

  const fabricantesOptions = useMemo(() => {
    if (!Array.isArray(templates)) return [];
    return templates.map((item) => Object.keys(item)[0]).filter(Boolean);
  }, [templates]);

  useEffect(() => {
    if (open && hostData) {
      setActiveStep(0);
      
      const backendFab = hostData.fabricante || "";
      const matchedFab = findCaseInsensitive(backendFab, fabricantesOptions);

      let matchedModel = "";
      const backendModel = hostData.modelo || "";
      if (matchedFab) {
        const templateObj = templates.find((t) => t[matchedFab]);
        if (templateObj && Array.isArray(templateObj[matchedFab])) {
          const modelOptions = templateObj[matchedFab].map((m) => m.modelo);
          matchedModel = findCaseInsensitive(backendModel, modelOptions);
          setModelosDisponiveis(modelOptions);
        }
      } else {
        setModelosDisponiveis([]);
      }

      const zabbixProxy = proxys.find(
        (p) => p.name.toLowerCase() === "zabbix-proxy",
      );
      let matchedProxyId = zabbixProxy ? zabbixProxy.proxyid : "0";

      const backendProxy = hostData.coletor || hostData.proxy;
      if (backendProxy) {
        const foundById = proxys.find((p) => p.proxyid == backendProxy);
        if (foundById) {
          matchedProxyId = foundById.proxyid;
        } else {
          const foundByName = proxys.find(
            (p) =>
              p.name.toLowerCase() === backendProxy.toString().toLowerCase(),
          );
          if (foundByName) {
            matchedProxyId = foundByName.proxyid;
          }
        }
      }

      setForm({
        id: hostData.id || hostData.hostid,
        nome: hostData.nome || "",
        ip: hostData.ip || "",
        comunidadeSnmp: hostData.comunidade || hostData.comunidadeSnmp || "",
        porta: hostData.portaSnmp || hostData.porta || 161,
        fabricante: matchedFab,
        modelo: matchedModel,
        userSsh: hostData.userSsh || "",
        senhaSsh: hostData.senhaSsh || "",
        portaSsh: hostData.port_ssh || hostData.portaSsh || 22,
        httpAddress: hostData.httpAddress || "",
        httpUser: hostData.httpUser || "",
        httpPass: hostData.httpPass || "",
        proxy: matchedProxyId,
        locatedAt: hostData.locatedAt || "CLIENTE",
      });

      setErrors({});

      const hostId = hostData.id || hostData.hostid;
      if (hostId) {
        setLoadingMacros(true);
        setPingHabilitado(false);
        setDestinosPing(Array(QUANTIDADE_DESTINOS_PING).fill(null));

        const fetchMacros = async () => {
          try {
            const payload = { hostId };
            const response = await api.post("/zabbix/hosts/macros", payload);
            if (response.data && response.data.macros) {
              const macros = response.data.macros;

              const pingHab = macros.find((m) => m.macro === MACRO_PING_HABILITADO);
              if (pingHab && pingHab.value === "1") {
                setPingHabilitado(true);
              }

              const newDestinos = Array(QUANTIDADE_DESTINOS_PING).fill(null);
              const newOrigens = Array(QUANTIDADE_DESTINOS_PING).fill("");

              for (let i = 0; i < QUANTIDADE_DESTINOS_PING; i++) {
                const destinoMacro = macros.find((m) => m.macro === MACROS_DESTINO[i]);
                const origemMacro = macros.find((m) => m.macro === MACROS_ORIGEM[i]);

                if (destinoMacro && destinoMacro.value) {
                  const preset = pingIcons.find((p) => p.value === destinoMacro.value);
                  if (preset) newDestinos[i] = preset;
                }
                if (origemMacro && origemMacro.value) {
                  newOrigens[i] = origemMacro.value;
                }
              }

              const httpAddressMacro = macros.find((m) => m.macro === "{$HTTP_ADDRESS}");
              const httpUserMacro = macros.find((m) => m.macro === "{$HTTP_USER}");
              const httpPassMacro = macros.find((m) => m.macro === "{$HTTP_PASS}");

              const sshUserMacro = macros.find((m) => m.macro === "{$SSH_USER}");
              const sshPassMacro = macros.find((m) => m.macro === "{$SSH_PASS}");
              const sshPortMacro = macros.find((m) => m.macro === "{$SSH_PORT}");

              setForm((prev) => ({
                ...prev,
                httpAddress: httpAddressMacro ? httpAddressMacro.value : prev.httpAddress,
                httpUser: httpUserMacro ? httpUserMacro.value : prev.httpUser,
                httpPass: httpPassMacro ? httpPassMacro.value : prev.httpPass,
                userSsh: sshUserMacro ? sshUserMacro.value : prev.userSsh,
                senhaSsh: sshPassMacro ? sshPassMacro.value : prev.senhaSsh,
                portaSsh: sshPortMacro ? sshPortMacro.value : prev.portaSsh,
              }));

              setDestinosPing(newDestinos);
              setOrigensPing(newOrigens);
            }
          } catch (error) {
            console.error("Erro ao buscar macros:", error);
          } finally {
            setLoadingMacros(false);
          }
        };

        fetchMacros();
      }
    }
  }, [open, hostData, templates, fabricantesOptions, proxys, pingIcons]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "fabricante") {
      const template = templates.find((t) => Object.keys(t)[0] === value);
      const modelos = template
        ? template[value].map((m) => m.modelo).sort((a, b) => a.localeCompare(b))
        : [];
      setModelosDisponiveis(modelos);
      setForm((prev) => ({ ...prev, fabricante: value, modelo: "" }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.nome) newErrors.nome = "Nome é obrigatório";
    if (!form.ip) newErrors.ip = "IP é obrigatório";
    else if (!validarIp(form.ip)) newErrors.ip = "IP inválido";
    if (!form.fabricante) newErrors.fabricante = "Fabricante é obrigatório";
    if (!form.modelo) newErrors.modelo = "Modelo é obrigatório";

    if (form.userSsh || form.senhaSsh || form.portaSsh !== 22) {
      if (!form.senhaSsh) newErrors.senhaSsh = "Senha SSH é obrigatória";
      if (!form.portaSsh) newErrors.portaSsh = "Porta SSH é obrigatória";
    }

    let pingsValidos = true;
    if (pingHabilitado) {
      const novosErrosOrigens = [...origensErrors];
      destinosPing.forEach((preset, idx) => {
        if (preset && !origensPing[idx]) {
          novosErrosOrigens[idx] = "Origem obrigatória para este destino";
          pingsValidos = false;
        } else if (preset && origensPing[idx] && !validarIp(origensPing[idx])) {
          novosErrosOrigens[idx] = "IP inválido";
          pingsValidos = false;
        } else {
          novosErrosOrigens[idx] = "";
        }
      });
      setOrigensErrors(novosErrosOrigens);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && pingsValidos;
  };

  const handleNextOrSave = async () => {
    if (activeStep === 0) {
      if (!validate()) return;

      const pingMacros = [];
      const disabledPingMacros = [];
      
      if (pingHabilitado) {
        destinosPing.forEach((preset, idx) => {
          if (preset) {
            pingMacros.push({
              macroIP: MACROS_ORIGEM[idx],
              valueIP: origensPing[idx] || "",
              macroPort: MACROS_DESTINO[idx],
              valuePort: preset.value,
            });
          }
        });
      } else {
        for (let i = 0; i < QUANTIDADE_DESTINOS_PING; i++) {
          disabledPingMacros.push({ macro: MACROS_ORIGEM[i], value: "" });
          disabledPingMacros.push({ macro: MACROS_DESTINO[i], value: "" });
        }
      }

      const fabricanteData = templates.find((t) => Object.keys(t)[0] === form.fabricante);
      const predefinicao = fabricanteData?.[form.fabricante]?.find((m) => m.modelo === form.modelo);
      let parsedTemplates = [];
      let parsedGroups = [];
      if (predefinicao) {
        try { parsedTemplates = JSON.parse(predefinicao.templates); } catch(e) {}
        try { parsedGroups = JSON.parse(predefinicao.groups); } catch(e) {}
      }

      const finalObject = {
        ...form,
        id: form.id,
        nome: form.nome,
        host: form.nome,
        httpAddress: form.httpAddress,
        httpUser: form.httpUser,
        httpPass: form.httpPass,
        pingHabilitado: pingHabilitado,
        destinosPing: destinosPing.map((preset, idx) => ({
            valor: preset?.value || "",
            descricao: preset?.label || "",
            origem: origensPing[idx] || ""
        })),
        templates: parsedTemplates,
        groups: parsedGroups,
        tags: [
          { fabricante: form.fabricante },
          { modelo: form.modelo },
          { funcao: form.modelo.split(" - ")[0] },
          { locatedAt: form.locatedAt },
        ],
        proxy_hostid: form.proxy !== "0" ? form.proxy : undefined,
      };

      const success = await onSave(finalObject);
      if (success) {
        setActiveStep(1);
      }
    }
  };

  const handleClose = () => {
    onClose();
  };

  const renderStep0 = () => {
    if (loadingMacros) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "350px" }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
      <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", fontWeight: 600, color: theme.palette.colors?.gray?.medium || "#666" }}>
          <DnsIcon sx={{ mr: 1, color: "primary.main" }} fontSize="small" /> Dados do Equipamento
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.colors?.gray?.medium || "#666", mb: 0.5, mr: 0.5 }}>
            Localização
          </Typography>
          <ToggleButtonGroup
            size="small"
            color="primary"
            value={form.locatedAt}
            exclusive
            onChange={(e, val) => val && handleChange({ target: { name: "locatedAt", value: val } })}
            aria-label="Localização do Equipamento"
            sx={{
              height: "36px",
              backgroundColor: alpha(theme.palette.background.default, 0.6),
              borderRadius: "12px",
              padding: "3px",
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiToggleButtonGroup-grouped": {
                margin: "0 4px",
                border: 0,
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.8rem",
                padding: "4px 12px",
                "&:not(:first-of-type)": {
                  borderRadius: "8px",
                },
                "&:first-of-type": {
                  borderRadius: "8px",
                },
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                },
              },
            }}
          >
            <ToggleButton value="PROVEDOR">
              <BusinessIcon sx={{ mr: 1, fontSize: "1.1rem" }} /> Provedor
            </ToggleButton>
            <ToggleButton value="CLIENTE">
              <PersonPinIcon sx={{ mr: 1, fontSize: "1.1rem" }} /> Cliente
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Nome *"
            name="nome"
            variant="filled"
            sx={textFieldStyle}
            value={form.nome}
            onChange={handleChange}
            error={!!errors.nome}
            helperText={errors.nome}
            autoComplete="off"
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="IP *"
            name="ip"
            variant="filled"
            sx={textFieldStyle}
            value={form.ip}
            onChange={handleChange}
            error={!!errors.ip}
            helperText={errors.ip}
            autoComplete="off"
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Comunidade SNMP"
            name="comunidadeSnmp"
            variant="filled"
            sx={textFieldStyle}
            value={form.comunidadeSnmp}
            onChange={handleChange}
            autoComplete="off"
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Porta SNMP *"
            name="porta"
            type="number"
            variant="filled"
            sx={textFieldStyle}
            value={form.porta}
            onChange={handleChange}
            autoComplete="off"
            size="small"
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl variant="filled" sx={textFieldStyle} size="small" fullWidth error={!!errors.fabricante}>
            <InputLabel>Fabricante *</InputLabel>
            <Select
              label="Fabricante *"
              name="fabricante"
              value={form.fabricante}
              onChange={handleChange}
            >
              {fabricantesOptions.map((fab) => (
                <MenuItem key={fab} value={fab}>
                  {fab}
                </MenuItem>
              ))}
            </Select>
            {errors.fabricante && <FormHelperText>{errors.fabricante}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl variant="filled" sx={textFieldStyle} size="small" fullWidth error={!!errors.modelo} disabled={!form.fabricante}>
            <InputLabel>Modelo *</InputLabel>
            <Select label="Modelo *" name="modelo" value={form.modelo} onChange={handleChange}>
              {modelosDisponiveis.map((modelo) => (
                <MenuItem key={modelo} value={modelo}>
                  {modelo}
                </MenuItem>
              ))}
            </Select>
            {errors.modelo && <FormHelperText>{errors.modelo}</FormHelperText>}
          </FormControl>
        </Grid>

        {enablePings && (
          <>
            <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={pingHabilitado}
                    onChange={(e) => setPingHabilitado(e.target.checked)}
                    color="primary"
                  />
                }
                label={<Typography color="textPrimary">Habilitar Ping</Typography>}
              />
              {isAdmin && (
                <Tooltip title="Gerenciar ícones de status de ping" arrow>
                  <IconButton
                    size="small"
                    onClick={() => window.open("/interno/administracao/geral/pingIcons", "_blank")}
                    sx={{ color: "primary.main" }}
                  >
                    <TuneIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
            {pingHabilitado &&
              destinosPing.map((selected, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={6}>
                    <PingPresetSelect
                      index={index}
                      value={selected}
                      options={pingIcons}
                      onChange={(newValue) => {
                        const novosDestinos = [...destinosPing];
                        novosDestinos[index] = newValue;
                        setDestinosPing(novosDestinos);
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="filled"
                      sx={textFieldStyle}
                      label={`Origem ${String.fromCharCode(65 + index)}`}
                      value={origensPing[index]}
                      onChange={(e) => {
                        const newOrigens = [...origensPing];
                        newOrigens[index] = e.target.value;
                        setOrigensPing(newOrigens);
                        if (origensErrors[index]) {
                          const newErrors = [...origensErrors];
                          newErrors[index] = "";
                          setOrigensErrors(newErrors);
                        }
                      }}
                      error={!!origensErrors[index]}
                      helperText={origensErrors[index]}
                    />
                  </Grid>
                </React.Fragment>
              ))}
          </>
        )}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", mt: 2, color: theme.palette.colors.gray.medium,fontWeight: 600 }}>
            <TerminalIcon sx={{ mr: 1, color: "primary.main" }} fontSize="small" /> Configurações SSH (Opcional)
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="User SSH"
            name="userSsh"
            variant="filled"
            sx={textFieldStyle}
            value={form.userSsh}
            onChange={handleChange}
            autoComplete="off"
            size="small"
          />
        </Grid>
        <Grid item xs={8}>
          <TextField
            fullWidth
            label={`Senha SSH ${form.userSsh ? "*" : ""}`}
            name="senhaSsh"
            variant="filled"
            sx={textFieldStyle}
            value={form.senhaSsh}
            onChange={handleChange}
            error={!!errors.senhaSsh}
            helperText={errors.senhaSsh}
            autoComplete="off"
            size="small"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            type="number"
            label={`Porta SSH ${form.userSsh ? "*" : ""}`}
            name="portaSsh"
            variant="filled"
            sx={textFieldStyle}
            value={form.portaSsh}
            onChange={handleChange}
            error={!!errors.portaSsh}
            helperText={errors.portaSsh}
            autoComplete="off"
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", mt: 2, fontWeight: 600, color: theme.palette.colors?.gray?.medium || "#666", }}>
            <HttpOutlinedIcon sx={{ mr: 1,  color: "primary.main", }} fontSize="small" /> Acesso HTTP (Opcional)
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Endereço (URL ou IP)"
            name="httpAddress"
            variant="filled"
            sx={textFieldStyle}
            value={form.httpAddress}
            onChange={handleChange}
            autoComplete="off"
            size="small"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Usuário HTTP"
            name="httpUser"
            variant="filled"
            sx={textFieldStyle}
            value={form.httpUser}
            onChange={handleChange}
            autoComplete="off"
            size="small"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Senha HTTP"
            name="httpPass"
            variant="filled"
            sx={textFieldStyle}
            value={form.httpPass}
            onChange={handleChange}
            autoComplete="off"
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl variant="filled" sx={textFieldStyle} size="small" fullWidth>
            <InputLabel>Proxy</InputLabel>
            <Select label="Proxy" name="proxy" value={form.proxy} onChange={handleChange}>
              {proxys.map((proxy) => (
                <MenuItem key={proxy.proxyid} value={proxy.proxyid}>
                  {proxy.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    );
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper sx={style}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: theme.palette.white.main }}>
          Editar Host
        </Typography>

        <Paper
          elevation={0}
          sx={{
            padding: "20px",
            borderRadius: "16px",
            mb: 1,
            background: theme.palette.background.modalContrast,
          }}
        >
          <Stepper activeStep={activeStep} sx={{ "& .MuiStepIcon-root.Mui-active": { color: "primary.main" } }}>
            <Step>
              <StepLabel>Dados do Host</StepLabel>
            </Step>
            <Step>
              <StepLabel>Vincular Clientes</StepLabel>
            </Step>
          </Stepper>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            padding: "20px",
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            background: theme.palette.background.modalContrast,
          }}
        >
        <Box sx={{ minHeight: "350px", maxHeight: "60vh", overflowY: "auto", pr: 1, overflowX: "hidden" }}>
          {activeStep === 0 ? (
            renderStep0()
          ) : (
            <VincularClientesStep
              hostId={form.id}
              groupId={hostData?.groupId || hostData?.groups?.[0]?.groupid || null}
              hostGroups={hostData?.groups || []}
              hostFuncao={hostData?.funcao || ""}
              hostModelo={hostData?.modelo || ""}
              hostClientsMap={hostClientsMap}
              setHostClientsMap={setHostClientsMap}
            />
          )}
        </Box>
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          {activeStep === 0 ? (
            <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
              <Button variant="outlined" onClick={handleClose} disabled={loading}>
                Cancelar
              </Button>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button variant="outlined" onClick={() => setActiveStep(1)} disabled={loading || loadingMacros}>
                  Pular para Vinculação
                </Button>
                <Button variant="contained" onClick={handleNextOrSave} disabled={loading || loadingMacros}>
                  Salvar Host e Avançar
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Button variant="outlined" onClick={handleClose}>
                Cancelar
              </Button>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button variant="outlined" onClick={() => setActiveStep(0)}>
                  Voltar
                </Button>
                <Button variant="contained" onClick={handleClose}>
                  Concluir
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Modal>
  );
}
